import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './AdminQueries.css';

const AdminQueries = () => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchQueries();
  }, [navigate]);

  const fetchQueries = async () => {
    try {
      setLoading(true);
      const response = await api.get('/queries');
      if (response.data.success) {
        setQueries(response.data.data);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching queries:', err);
      setError('Failed to load queries. Please check if backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const response = await api.put(`/queries/${id}/status`, { status: newStatus });
      if (response.data.success) {
        fetchQueries(); // Refresh the list
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const deleteQuery = async (id) => {
    if (!id) {
      alert('Error: No query ID provided');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this query?')) {
      return;
    }

    try {
      const response = await api.delete(`/queries/${id}`);

      // Check if delete was successful
      if (response.status === 200 || (response.data && response.data.success)) {
        // Remove the query from the list immediately for better UX
        setQueries(prevQueries => prevQueries.filter(q => {
          const queryId = q._id || q.id;
          return queryId !== id;
        }));
        // Refresh to sync with server
        setTimeout(() => {
          fetchQueries();
        }, 100);
      } else {
        fetchQueries();
      }
    } catch (err) {
      console.error('Error deleting query:', err);

      if (err.response?.status === 404) {
        alert('Query not found. It may have already been deleted.');
        fetchQueries(); // Refresh to sync with server
      } else if (err.response?.status === 401) {
        alert('Unauthorized. Please log in again.');
      } else if (err.response?.status === 400) {
        alert(`Invalid request: ${err.response?.data?.message || 'Invalid query ID'}`);
      } else {
        const errorMsg = err.response?.data?.message || err.message || 'Unknown error';
        alert(`Failed to delete query: ${errorMsg}`);
      }
    }
  };

  const filteredQueries = selectedStatus === 'all'
    ? queries
    : queries.filter(q => q.status === selectedStatus);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'new':
        return 'status-badge new';
      case 'read':
        return 'status-badge read';
      case 'replied':
        return 'status-badge replied';
      default:
        return 'status-badge';
    }
  };

  if (loading) {
    return (
      <div className="admin-queries-container">
        <div className="loading">Loading queries...</div>
      </div>
    );
  }

  return (
    <div className="admin-queries-container">
      <div className="admin-header">
        <h1>Contact Form Queries</h1>
        <div className="admin-header-actions">
          <button onClick={fetchQueries} className="refresh-btn">🔄 Refresh</button>
          <button onClick={handleLogout} className="logout-btn">🚪 Logout</button>
        </div>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="filter-section">
        <label>Filter by status:</label>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="status-filter"
        >
          <option value="all">All ({queries.length})</option>
          <option value="new">New ({queries.filter(q => q.status === 'new').length})</option>
          <option value="read">Read ({queries.filter(q => q.status === 'read').length})</option>
          <option value="replied">Replied ({queries.filter(q => q.status === 'replied').length})</option>
        </select>
      </div>

      {filteredQueries.length === 0 ? (
        <div className="no-queries">No queries found.</div>
      ) : (
        <div className="queries-list">
          {filteredQueries.map((query) => (
            <div key={query._id} className="query-card">
              <div className="query-header">
                <div className="query-info">
                  <h3>{query.name}</h3>
                  <p className="query-email">{query.email}</p>
                  <p className="query-date">{formatDate(query.createdAt)}</p>
                </div>
                <div className="query-actions">
                  <span className={getStatusBadgeClass(query.status)}>
                    {query.status.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="query-message">
                <p>{query.message}</p>
              </div>

              <div className="query-footer">
                <div className="status-buttons">
                  <button
                    onClick={() => updateStatus(query._id, 'new')}
                    className={`status-btn ${query.status === 'new' ? 'active' : ''}`}
                  >
                    New
                  </button>
                  <button
                    onClick={() => updateStatus(query._id, 'read')}
                    className={`status-btn ${query.status === 'read' ? 'active' : ''}`}
                  >
                    Read
                  </button>
                  <button
                    onClick={() => updateStatus(query._id, 'replied')}
                    className={`status-btn ${query.status === 'replied' ? 'active' : ''}`}
                  >
                    Replied
                  </button>
                </div>
                <button
                  onClick={() => deleteQuery(query._id)}
                  className="delete-btn"
                  type="button"
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminQueries;
